import React, { useState } from "react";
import { Popup, Icon, Divider, Button, Placeholder } from "semantic-ui-react";
import { ContactAvatar } from "../../permits/components/ContactAvatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getPhysicalAddressText, makeFullAddress } from "../../../utils/functions";
import { faBriefcase, faEnvelope, faIdBadge } from "@fortawesome/pro-regular-svg-icons";
import { ParsedPhoneNumber } from "../../../components/global_components/form_components/ControlledPhoneNumber";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { getPerson, Person } from "../../../queries/contacts";
import { EditContactModal } from "./EditContactModal";
import { faHotel, faMailbox, faMapMarker, faPhoneAlt } from "@fortawesome/pro-solid-svg-icons";
import { format, parseISO } from "date-fns";
import { useCoreDepartment } from "../../../hooks/useDepartments";
import permissionsSelectors from "../../../selectors/permissions";
import { Permissions } from "../../../types/auth/Permission";
import { LinkSubscriberContactMethodModal } from "../../notifications/routes/notify/subscribers/modals/LinkSubscriberContactMethodModal";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { ContactFieldType } from "../../../queries/crmTopics";

interface IContactConnectedProfilePopupProps {
  personId?: number | null;
  contact?: { userId: number | null; contactType: string | null } | null;
  trigger: JSX.Element;
  showBanner?: boolean;
  enableEdit?: boolean;
  onClose?: () => void;
  onEdit?: (data: Person) => void;
  subscriberContactMethodId?: number; // To link a Notify V2 subscriber contact method to the person
  editRequiredFields?: ContactFieldType[];
  triggerDelay?: number;
  open?: boolean;
  popupPosition?:
    | "bottom left"
    | "top left"
    | "top right"
    | "bottom right"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
}

export default function ContactConnectedProfilePopup({
  personId,
  contact,
  trigger,
  showBanner = true,
  enableEdit = false,
  onEdit,
  subscriberContactMethodId,
  triggerDelay = 500,
  open,
  popupPosition = "bottom left",
  onClose,
  editRequiredFields,
}: IContactConnectedProfilePopupProps) {
  const [loadCitizen, setLoadCitizen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [isLinkContactMethodModalOpen, setIsLinkContactMethodModalOpen] = useState<boolean>(false);
  const { site } = useParams<{ site: string }>();
  const networkHeaders = useNetworkHeaders();

  const { isEnabled: hasNotifyV2 } = useFeatureFlag("notify.portal");

  const actualPersonId = personId ?? contact?.userId;
  const personQuery = useQuery(
    ["person", actualPersonId?.toString()],
    () => getPerson(networkHeaders)(actualPersonId!),
    {
      enabled: (loadCitizen || open) && actualPersonId,
    },
  );
  const person = personQuery.data || null;
  const isEmployee = person?.personType == "employee";
  const { departmentQuery } = useCoreDepartment((isEmployee && person?.departmentId) || undefined);
  const userCanEditEmployee =
    permissionsSelectors.can(Permissions.MANAGE_EMPLOYEES) && person?.username !== "govout143";
  if (!actualPersonId) return trigger;

  const handleEditSuccess = (data: Person) => {
    personQuery.refetch();
    onEdit?.(data);
  };

  if (!person) {
    return (
      <Popup
        style={{ padding: "0", border: "1px solid #DBDDDE", borderRadius: "8px", overflow: "hidden", zIndex: 3000 }}
        basic
        position='bottom left'
        hoverable
        mouseEnterDelay={triggerDelay}
        mouseLeaveDelay={200}
        trigger={trigger}
        onOpen={() => setLoadCitizen(true)}
        open={open}
      >
        <div className='contact-connected-profile-popup'>
          {showBanner && (
            <div className='connected-profile-container'>
              <div>
                <Icon name='sync alternate' />
              </div>
              <span>Connected Profile</span>
            </div>
          )}
          <div style={{ width: "100%", height: "250px", padding: 16 }}>
            <Placeholder>
              <Placeholder.Header image>
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Header>
            </Placeholder>
            <Placeholder>
              <Placeholder.Paragraph>
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Paragraph>
            </Placeholder>
          </div>
        </div>
      </Popup>
    );
  }

  const fullAddress = makeFullAddress(person.address, person.address2, person.city, person.state, person.zip);
  const physicalAddressText = getPhysicalAddressText(
    person.sameMailAndPhysicalAddress ?? false,
    person.homeAddress ?? "",
    person.homeAddress2 ?? "",
    person.homeCity ?? "",
    person.homeState ?? "",
    person.homeZip ?? "",
    !!person.address,
  );
  const mostRecentActivity = (() => {
    if (!person.lastWebAccessAt && !person.lastMobileAccessAt) return null;
    if (person.lastWebAccessAt && !person.lastMobileAccessAt) return parseISO(person.lastWebAccessAt);
    if (!person.lastWebAccessAt) return parseISO(person.lastMobileAccessAt!);
    return parseISO(person.lastWebAccessAt) > parseISO(person.lastMobileAccessAt!)
      ? parseISO(person.lastWebAccessAt)
      : parseISO(person.lastMobileAccessAt!);
  })();

  return (
    <>
      <LinkSubscriberContactMethodModal
        subscriberContactMethodId={subscriberContactMethodId}
        person={{
          userId: person.personId,
          firstName: person.firstName,
          lastName: person.lastName,
        }}
        isOpen={isLinkContactMethodModalOpen}
        close={() => {
          setIsLinkContactMethodModalOpen(false);
          onClose?.(); // Only call onClose if it exists
        }}
      />

      <Popup
        style={{
          padding: "0",
          border: "1px solid #DBDDDE",
          borderRadius: "8px",
          overflow: "hidden",
          minWidth: 280,
          width: "max-content",
          zIndex: 3000,
        }}
        basic
        position={popupPosition}
        hoverable
        mouseEnterDelay={500}
        mouseLeaveDelay={200}
        trigger={trigger}
        onClick={(e: any) => e.stopPropagation()}
        open={open}
      >
        <div className='tw-min-w-[280px] tw-space-y-[16px] tw-p-[21px]'>
          {/* Name Header */}
          <div className='tw-flex tw-items-center tw-gap-[7px]'>
            <ContactAvatar size='32px' firstName={person.firstName} lastName={person.lastName} aria-hidden='false' />
            <div className='tw-flex tw-flex-col'>
              {contact?.contactType && (
                <span className='tw-text-[9px] tw-font-bold tw-uppercase tw-leading-[18px] tw-tracking-[0.63px]'>
                  {contact?.contactType}
                </span>
              )}
              <span className='tw-text-[16px] tw-font-bold tw-leading-[20px] tw-tracking-[0.16px]'>
                {person.firstName} {person.lastName}
              </span>
            </div>
          </div>
          <Divider />

          {/* Content */}
          <div className='tw-space-y-[16px]'>
            {/* Contact Information */}
            <div className='tw-flex tw-flex-col tw-gap-4 [&>div]:tw-flex [&>div]:tw-items-center [&>div]:tw-gap-[4px] [&>div]:tw-align-middle [&_span]:tw-text-[13px] [&_span]:tw-font-bold [&_span]:tw-text-text-secondary [&_svg]:tw-m-0 [&_svg]:tw-aspect-square [&_svg]:tw-size-[16px] [&_svg]:tw-p-0'>
              {isEmployee && departmentQuery.data && (
                <Popup
                  content='Department'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faHotel} />
                      <span className='tw-truncate'>{departmentQuery.data?.name}</span>
                    </div>
                  }
                />
              )}
              {person.email && (
                <Popup
                  content='Email Address'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faEnvelope} />
                      <span className='tw-truncate'>{person.email}</span>
                    </div>
                  }
                />
              )}
              {person.phone && (
                <Popup
                  content='Phone Number'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faPhoneAlt} />
                      <ParsedPhoneNumber value={person.phone} />
                    </div>
                  }
                />
              )}
              {fullAddress != "" && (
                <Popup
                  content='Mailing Address'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faMailbox} />
                      <span className='tw-truncate'>{fullAddress}</span>
                    </div>
                  }
                />
              )}
              {!!physicalAddressText && (
                <Popup
                  content='Physical Address'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faMapMarker} />
                      <span className='tw-truncate'>
                        {person.sameMailAndPhysicalAddress ? <i>{physicalAddressText}</i> : physicalAddressText}
                      </span>
                    </div>
                  }
                />
              )}
              {person.companyName && (
                <Popup
                  content='Company Name'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faBriefcase} />
                      <span className='tw-truncate'>{person.companyName}</span>
                    </div>
                  }
                />
              )}
              {person.licenseNumber && (
                <Popup
                  content='License Number'
                  inverted
                  mouseEnterDelay={500}
                  position='left center'
                  trigger={
                    <div>
                      <FontAwesomeIcon icon={faIdBadge} />
                      <span className='tw-truncate'>{person.licenseNumber}</span>
                    </div>
                  }
                />
              )}
            </div>

            <Divider />

            {/* Actions */}
            <div className='tw-grid tw-grid-cols-[repeat(auto-fit,minmax(0,1fr))] tw-gap-[16px] [&_a]:tw-w-full [&_button]:tw-w-full [&_button]:tw-px-[16px] [&_button]:tw-py-[8px] [&_button]:tw-text-[14px] [&_button]:tw-font-semibold [&_button]:tw-leading-[20px]'>
              {enableEdit && (
                <div>
                  {isEmployee && userCanEditEmployee ? (
                    <Link to={`/${site}/admin/employees/${actualPersonId}`}>
                      <Button
                        basic
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        Edit Profile
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      basic
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setEditModalOpen(true);
                      }}
                      disabled={isEmployee && !userCanEditEmployee}
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              )}
              {subscriberContactMethodId && hasNotifyV2 && (
                <Button basic onClick={() => setIsLinkContactMethodModalOpen((prev) => !prev)}>
                  Link Contact
                </Button>
              )}
              {isEmployee ? (
                <Link to={`/${site}/admin/employees/${actualPersonId}`}>
                  <Button basic>View Profile</Button>
                </Link>
              ) : (
                <Link to={`/${site}/contacts/${actualPersonId}`}>
                  <Button basic>View Profile</Button>
                </Link>
              )}
            </div>

            {/* Last Activity */}
            <div className='tw-flex tw-flex-col tw-gap-1 tw-font-semibold tw-leading-[16px]'>
              <span className='tw-text-[12.5px] tw-tracking-[0.25px]'>Last Activity</span>
              <span className='tw-text-[11px] tw-tracking-[0.22px] tw-text-primary-hint'>
                {mostRecentActivity ? format(mostRecentActivity, "M/d/yyyy, h:mm:ss a") : "None"}
              </span>
            </div>
          </div>
        </div>
      </Popup>

      {/* Edit Modal */}
      {enableEdit && person && editModalOpen && (
        <EditContactModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          personId={actualPersonId}
          onSuccess={handleEditSuccess}
          requiredFields={editRequiredFields}
        />
      )}
    </>
  );
}
